import * as React from 'react';

import { classNames } from '@core/utils/functions/class-names';
import Link from 'next/link';

import ByLine from '@/components/pages/frontpage/components/Feature/Middle/ByLine';
import type { ArticleType } from '@/types/ArticleType';
import ImageWithFallback from '@core/components/display/images/ImageWithFallback';
import slugify from '@core/utils/functions/slugify';
import decodeEntities from '@/utils/functions/string/decodeEntities';

type NewsSectionProps = {
    className?: string;
    article: ArticleType;
};

const NewsSection = (props: NewsSectionProps) => {
    const { className, article } = props;

    return (
        <div className={classNames('flex flex-col justify-start self-stretch flex-grow gap-2', className)}>
            <Link href={`/news/${article.slug}`} passHref>
                <a className="cursor-pointer">
                    <ImageWithFallback
                        src={article.imageUrl}
                        fallbackSrc="/images/purple-placeholder.png"
                        width={384}
                        height={216}
                        sizes="(min-width: 75em) 10vw"
                        decoding="async"
                        quality={55}
                        alt="article-image"
                    />
                </a>
            </Link>
            <div className="flex justify-start items-center self-stretch flex-grow-0 flex-shrink-0 gap-1">
                <p className="flex whitespace-nowrap overflow-auto scrollbar-hide flex-grow text-[10px] text-left uppercase text-primary gap-2">
                    {article.categories.slice(0, 2).map(category =>
                        category.toLowerCase() === 'sponsored' ? (
                            <span key={category} className="text-gray-700">
                                {category}
                            </span>
                        ) : (
                            <Link key={category} href={`/category/${slugify(decodeEntities(category))}`} passHref>
                                <a>{decodeEntities(category)}</a>
                            </Link>
                        )
                    )}
                </p>
            </div>
            <div className="flex justify-start items-start self-stretch flex-grow-0 flex-shrink-0 gap-2.5">
                <Link href={`/news/${article.slug}`} passHref>
                    <a className="font-headline flex-grow text-base font-semibold leading-snug hover:text-primary">
                        {article.title}
                    </a>
                </Link>
            </div>
            <div className="flex justify-start items-start self-stretch flex-grow-0 flex-shrink-0 gap-2.5">
                <p className="flex-grow text-xs text-left text-light-gray text-light-gray">{article.excerpt}</p>
            </div>
            <ByLine
                className="text-xxs"
                authors={article.authors}
                publishedAtTimestamp={article.publishedAtTimestamp}
            />
        </div>
    );
};

export default NewsSection;
